import './SourceBrandsTable.scss';
import React, { useMemo, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Skeleton } from 'primereact/skeleton';
import { useTranslation } from 'react-i18next';
import { SearchFilter } from '../../../components/search-filter/SearchFilter';
import EmptyPanel from '../../../../../shared/components/empty-panel/EmptyPanel';
import { BrandForSourceRow } from '../../../../../shared/services/monitor/data/sources/MonitorDataSourcesService';
import { KeywordsData } from '../../../../../shared/models/tags/TagsResponse';

interface SourceBrandsTableProps {
  brands: BrandForSourceRow[];
  isLoading: boolean;
  siteName: string;
  commonKeywords: KeywordsData[];
}

const SourceBrandsTable: React.FC<SourceBrandsTableProps> = ({ brands, isLoading, siteName, commonKeywords }) => {
  const { t } = useTranslation();
  const [keyword, setKeyword] = useState('');

  const keywordMap = useMemo(() => {
    const map = new Map<string, string>();
    commonKeywords.forEach((k) => map.set(k.normalizedKeyword, k.keyword));
    return map;
  }, [commonKeywords]);

  const resolveBrandName = (brand: string): string => {
    return keywordMap.get(brand) ?? brand;
  };

  const filteredBrands = useMemo(() => {
    if (!keyword.trim()) return brands;
    const lower = keyword.toLowerCase();
    return brands.filter((b) => {
      const displayName = resolveBrandName(b.brand);
      return displayName.toLowerCase().includes(lower) || b.brand.toLowerCase().includes(lower);
    });
  }, [brands, keyword, keywordMap]);

  return (
    <div className="stats__panel source-brands-table">
      <div className="page-box__title-action">
        <div className="page-box__title">
          {t('sourceBrandsTitle', { siteName })}
        </div>
        <div className="page-box__source-filter">
          <SearchFilter value={keyword} onChange={setKeyword} className="search-filter--inline" />
        </div>
      </div>

      {isLoading ? (
        <Skeleton className="skeleton--table">
          <span />
          <span />
          <span />
          <span />
          <span />
          <span />
          <span />
        </Skeleton>
      ) : (
        <DataTable
          value={filteredBrands}
          stripedRows
          tableClassName="table"
          className="table-wrapper"
          tableStyle={{ width: '100%' }}
          paginator
          emptyMessage={() => <EmptyPanel />}
          paginatorClassName="pagination"
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          rows={10}
          rowsPerPageOptions={[10, 25, 50]}
          dataKey="brand"
          currentPageReportTemplate={t('rowsNumber') + ':'}
        >
          <Column
            field="brand"
            header={t('sourceBrandColumn')}
            headerClassName="table__header"
            style={{ minWidth: '200px' }}
            body={(rowData: BrandForSourceRow) => (
              <span className="source-brands-table__brand">{resolveBrandName(rowData.brand)}</span>
            )}
          />
          <Column
            field="position"
            header={t('sourceBrandPositionColumn')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            style={{ width: '120px', minWidth: '120px' }}
            sortable
            body={(rowData: BrandForSourceRow) => rowData.position ?? '—'}
          />
          <Column
            field="mentionCount"
            header={t('sourceBrandFrequencyColumn')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            style={{ width: '120px', minWidth: '120px' }}
            sortable
            body={(rowData: BrandForSourceRow) => rowData.mentionCount ?? '—'}
          />
        </DataTable>
      )}
    </div>
  );
};

export default SourceBrandsTable;
